import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Menu, X } from 'lucide-react';
import '../css/RibbonNavStyling.css';
import guest from '../images/guestProfilePicture.png';
import { useAuth } from '../hooks/useAuth';

const RibbonNav = ({ pageName }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const timeoutRef = useRef(null);
    const navigate = useNavigate();
    const { user, isLoggedIn, refresh } = useAuth();

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleMouseEnter = () => {
        if (isMobile) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            setIsExpanded(true);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (isMobile) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            setIsExpanded(false);
        }, 200);
    };

    const toggleMobileMenu = () => {
        if (showMobileMenu) {
            // start slide out
            setIsAnimatingOut(true);
            setTimeout(() => {
                setShowMobileMenu(false);
                setIsAnimatingOut(false);
            }, 300); // match animation duration
        } else {
            setShowMobileMenu(true);
        }
    };

    const closeMobileMenu = () => {
        if (showMobileMenu) {
            setIsAnimatingOut(true);
            setTimeout(() => {
                setShowMobileMenu(false);
                setIsAnimatingOut(false);
            }, 300);
        }
    };

    const goHome = () => {
        navigate('/');
        if (isMobile) {
            closeMobileMenu();
        }
    };

    const goAbout = () => {
        navigate('/about');
        if (isMobile) {
            closeMobileMenu();
        }
    };

    const goDashboard = () => {
        navigate('/dashboard');
        if (isMobile) {
            closeMobileMenu();
        }
    };

    const handleLogout = async () => {
        try {
            await fetch('/api/logout', {
                method: 'POST',
            });

            await refresh();
            navigate('/');
            if (isMobile) {
                closeMobileMenu();
            }
        } catch (err) {
            console.error('Logout failed:', err);
        }
    };

    if (isMobile) {
        return (
            <>
                <div className="mobile-ribbon-wrapper">
                    <div className="mobile-ribbon-content">
                        <span className="mobile-title">Planted Garden Designer</span>
                        <button
                            className="mobile-menu-button"
                            onClick={toggleMobileMenu}
                            aria-label="Toggle menu"
                        >
                            {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>

                    {/* Mobile Menu Overlay */}
                    {showMobileMenu && (
                        <>
                            <div className="mobile-menu-overlay" onClick={closeMobileMenu}/>
                            <div className={`mobile-menu ${isAnimatingOut ? "slide-out" : "slide-in"}`}>
                                <div className="mobile-menu-header">
                                    <div className="mobile-profile-section">
                                        <img
                                            src={user?.profile_picture_url || guest}
                                            alt="Profile"
                                            className="mobile-profile-picture"
                                        />
                                        <span className="mobile-page-name">{pageName}</span>
                                    </div>
                                    <button
                                        className="mobile-close-button"
                                        onClick={closeMobileMenu}
                                        aria-label="Close menu"
                                    >
                                        <X size={20}/>
                                    </button>
                                </div>

                                <nav className="mobile-nav-list">
                                    <button
                                        className="mobile-nav-item"
                                        onClick={goHome}
                                    >
                                        Home
                                    </button>
                                    <button
                                        className="mobile-nav-item"
                                        onClick={goAbout}
                                    >
                                        About
                                    </button>
                                    <button
                                        className="mobile-nav-item"
                                        onClick={goDashboard}
                                    >
                                        Dashboard
                                    </button>
                                    {isLoggedIn && (
                                        <button
                                            className="mobile-nav-item logout"
                                            onClick={handleLogout}
                                        >
                                            Log Out
                                        </button>
                                    )}
                                </nav>
                            </div>
                        </>
                    )}
                </div>
            </>
        );
    }

    // Desktop version
    return (
        <div
            className={`ribbon-wrapper ${isExpanded ? 'expanded' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <span className="title">Planted Garden Designer | {pageName}</span>

            <div className="nav-buttons">
                <button className="nav-btn" onClick={goHome}>Home</button>
                <button className="nav-btn" onClick={goAbout}>About</button>
                <button className="nav-btn" onClick={goDashboard}>Dashboard</button>
                {isLoggedIn && (
                    <button className="nav-btn" onClick={handleLogout}>Log Out</button>
                )}
            </div>

            <div className="profile-picture">
                <img
                    src={user?.profile_picture_url || guest}
                    alt="Profile"
                    className="user-profile-picture"
                />
            </div>

            <span className="collapse-icon">◀</span>
            <span className="expand-icon">▶</span>
        </div>
    );
};

export default RibbonNav;